"use client";
import React, { useState } from "react";
import { resendLeadOtp, verifyLeadOtp } from "@/app/utilities/apis";

interface LeadOtpStepProps {
    leadId: string;
    phone: string;
    onVerified: () => void;
    onCancel?: () => void;
}

// Reuses the Login Screen OTP layer's UI/UX (4-digit input, resend timer,
// 5-attempt lockout with a 15-minute cooldown, error states) — see
// src/component/LoginSignup.tsx's 'otp' step — but verifies against a
// specific lead (via verifyLeadOtp/resendLeadOtp) instead of doing the
// user-account/JWT side effects that the login flow does.
const LeadOtpStep: React.FC<LeadOtpStepProps> = ({ leadId, phone, onVerified, onCancel }) => {
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [otpError, setOtpError] = useState("");
    const [otpAttempts, setOtpAttempts] = useState(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [blockTimer, setBlockTimer] = useState(0);
    const [resendTimer, setResendTimer] = useState(60);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);

    React.useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    React.useEffect(() => {
        if (isBlocked && blockTimer > 0) {
            const timer = setTimeout(() => setBlockTimer(blockTimer - 1), 1000);
            return () => clearTimeout(timer);
        } else if (isBlocked && blockTimer === 0) {
            setIsBlocked(false);
            setOtpAttempts(0);
            setOtpError("");
        }
    }, [isBlocked, blockTimer]);

    const handleOtpChange = (index: number, value: string) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value && index < 3) {
                const nextInput = document.getElementById(`lead-otp-input-${index + 1}`);
                if (nextInput) (nextInput as HTMLInputElement).focus();
            }
        }
    };

    const handleVerify = async () => {
        if (isBlocked) {
            setOtpError(`Too many failed attempts. Please try again in ${Math.floor(blockTimer / 60)}:${(blockTimer % 60).toString().padStart(2, "0")}`);
            return;
        }

        setIsVerifying(true);
        try {
            const otpCode = otp.join("");
            const response = await verifyLeadOtp(leadId, otpCode);
            if (response.status === "success") {
                setOtpError("");
                setOtpAttempts(0);
                onVerified();
            } else {
                const newAttempts = otpAttempts + 1;
                setOtpAttempts(newAttempts);
                if (newAttempts >= 5) {
                    setIsBlocked(true);
                    setBlockTimer(15 * 60);
                    setOtpError("Too many failed attempts. Please try again after 15 minutes.");
                } else {
                    setOtpError(response.message || `Invalid OTP. ${5 - newAttempts} attempts remaining.`);
                }
            }
        } catch {
            setOtpError("Something went wrong. Please try again.");
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResend = async () => {
        if (isBlocked) {
            setOtpError(`Too many failed attempts. Please try again in ${Math.floor(blockTimer / 60)}:${(blockTimer % 60).toString().padStart(2, "0")}`);
            return;
        }

        setIsResending(true);
        try {
            const response = await resendLeadOtp(leadId);
            if (response.status === "success") {
                setResendTimer(60);
                setOtp(["", "", "", ""]);
                setOtpError("");
            } else {
                setOtpError(response.message || "Failed to resend OTP. Please try again.");
            }
        } catch {
            setOtpError("Failed to resend OTP. Please try again.");
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div>
            <h2 className="font-[Roboto] font-bold text-[20px] leading-8 mb-2 text-[#1F2937]">Verify your mobile number</h2>
            <p className="font-[Roboto] mb-6 text-[#6B7280] font-normal text-[14px] leading-[20px]">
                {`We've sent a 4-digit code to `}
                <span className="font-medium text-[#1F2937]">{phone}</span>
            </p>
            <div className="flex space-x-4 justify-between mb-6">
                {otp.map((digit, idx) => (
                    <input
                        key={idx}
                        id={`lead-otp-input-${idx}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        className="w-14 h-14 text-2xl text-center border border-gray-300 rounded-lg focus:outline-none focus:border-2 focus:border-[#008C8B]"
                    />
                ))}
            </div>
            {otpError && <div className="text-red-500 text-sm mb-2 text-center">{otpError}</div>}
            <button
                type="button"
                onClick={handleVerify}
                disabled={otp.some((d) => d === "") || isBlocked || isVerifying}
                className="w-full bg-[#008C8B] text-white py-3 rounded-md font-bold text-[16px] disabled:opacity-50 shadow-md mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
                {isVerifying ? "VERIFYING..." : "VERIFY"}
            </button>

            {resendTimer > 0 ? (
                <div className="font-[Roboto] text-center text-[14px] text-gray-600 mb-2 font-semibold">
                    {"Resend code in "}
                    <span className="text-black">{resendTimer}</span>
                    {" seconds"}
                </div>
            ) : (
                <div className="font-[Roboto] text-center text-sm mb-2">
                    <button type="button" className="text-[#3B82F6] font-semibold underline cursor-pointer" onClick={handleResend} disabled={isResending}>
                        {isResending ? "Resending..." : "Resend code"}
                    </button>
                </div>
            )}

            {onCancel && (
                <div className="text-center text-sm">
                    <button type="button" className="text-[#3B82F6] font-semibold cursor-pointer" onClick={onCancel}>
                        Change mobile number
                    </button>
                </div>
            )}
        </div>
    );
};

export default LeadOtpStep;
